/**
 * "Handleliste" — the shopping-list overlay (Ukeplanlegger, Slice 5, the
 * FINAL slice). Opened from `MealPlannerPanel`'s "Lag handleliste" button —
 * NEVER embedded inline (design handoff §5). Desktop: a centered modal.
 * Mobile: a full-screen view. Same markup for both — `styles/app.css` alone
 * decides the presentation via a media query, mirroring `AddToCalendarDialog`.
 *
 * Two independent sections (handoff §4 — do NOT blur this line):
 *  - "Fra oppskrifter": the server-merged, already-sorted `ShoppingListLine[]`
 *    — rendered EXACTLY in server order (no client re-sort/re-group/re-merge).
 *    Each line's amount uses the SHARED `formatIngredientAmount` (the same
 *    helper cook mode uses) so the two views never disagree about formatting.
 *  - "Annet": the manual items — a wholly separate bucket that must never be
 *    recomputed/merged/dropped when recipes or servings change. They come
 *    straight from the server's `manual` array.
 *
 * NO servings control anywhere here (an explicit user rejection — servings
 * are set on the calendar entry BEFORE the list is opened) and NO grouping by
 * recipe/category (also explicitly rejected — it would defeat cross-recipe
 * merging). Do not reintroduce either.
 *
 * Role-gating (UI mirror only — the API + RLS are authoritative): a
 * read/comment member sees the list but gets no checkbox/remove/add
 * affordance, and this component never issues a write call for them.
 *
 * Accessibility: a real modal dialog — `role="dialog"`, `aria-modal`,
 * `aria-labelledby` the title, initial focus on the close button, a Tab
 * focus-trap within the panel, Escape closes, and focus returns to whatever
 * triggered the overlay (the "Lag handleliste" button) on close/unmount. No
 * other overlay in this codebase implements this yet (`AddToCalendarDialog`
 * has none) — this is the reference implementation to reuse next time.
 *
 * Copy/share (send the list to whoever is shopping): both are READ-ONLY, so
 * unlike the checkbox/remove/add controls above they are available to EVERY
 * member regardless of role, including `read`/`comment` — do not gate them on
 * `canEdit`. The copied/shared text is a single flat list (recipe lines then
 * manual items, in on-screen order, `checked`/`removed` excluded from both
 * sections, NO section headings — the product owner found "Fra
 * oppskrifter"/"Annet" confusing pasted into a text message) built with the
 * SAME `lineAmountLabel` used for the on-screen amount so the two can never
 * disagree. Deliberately plain `- ` bullets, no `☐`/markdown checkbox syntax
 * — Apple Notes' checklist format is proprietary and no web page can emit
 * tappable checkboxes there; this was considered and rejected.
 *
 * iOS Safari gotcha: the clipboard write is issued SYNCHRONOUSLY inside the
 * click handler (no `await` before `navigator.clipboard.writeText(...)` is
 * called) — Safari revokes the user-gesture context across an await and the
 * write silently fails otherwise.
 */

import {
  type CookbookRole,
  type ManualShoppingItem,
  type ShoppingListLine,
  formatIngredientAmount,
  roleCanEditRecipes,
} from "@tjommi/shared";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  UnauthorizedError,
  addManualShoppingItem,
  deleteManualShoppingItem,
  fetchShoppingList,
  setShoppingItemState,
  updateManualShoppingItem,
} from "../api";
import { rangeLabelForMonday } from "../lib/week";

type LoadState =
  | { status: "loading" }
  | { status: "ok"; fromRecipes: ShoppingListLine[]; manual: ManualShoppingItem[] }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), " +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** How much (amount) text to show next to a merged line — "" when there's
 * nothing scalable to show at all (a unique, non-numeric contribution). See
 * `shopping-list.merge.ts`'s header for the full `quantity`/`count` rule. */
function lineAmountLabel(line: ShoppingListLine): string {
  if (line.quantity == null) {
    return line.count > 1 ? `×${line.count}` : "";
  }
  const base = formatIngredientAmount({ quantity: line.quantity, unit: line.unit });
  return line.count > 0 ? `${base} (+${line.count})` : base;
}

/** Feedback shown next to the copy/share buttons — clears itself on success,
 * stays put on error/empty until the next action replaces it. */
type CopyFeedback = { kind: "success" | "error" | "info"; text: string };

const COPY_SUCCESS_MESSAGE = "Kopiert!";
const COPY_ERROR_MESSAGE = "Kunne ikke kopiere listen. Prøv igjen.";
const SHARE_ERROR_MESSAGE = "Kunne ikke dele listen. Prøv igjen.";
const NOTHING_TO_COPY_MESSAGE = "Ingenting å kopiere.";
const COPY_FEEDBACK_CLEAR_MS = 2000;

export function ShoppingListOverlay({
  cookbookId,
  weekStart,
  role,
  accessToken,
  onClose,
  onSessionExpired,
}: {
  cookbookId: string;
  /** That week's Monday (`YYYY-MM-DD`) — reuse `lib/week.ts`, never recompute. */
  weekStart: string;
  role: CookbookRole;
  accessToken: string;
  onClose: () => void;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const canEdit = roleCanEditRecipes(role);
  const weekLabel = useMemo(() => rangeLabelForMonday(weekStart), [weekStart]);

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState("");
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });
    setMutationError(null);

    fetchShoppingList(cookbookId, weekStart, accessToken)
      .then((res) => {
        if (!ignore) setState({ status: "ok", fromRecipes: res.fromRecipes, manual: res.manual });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          setState({ status: "unauthorized" });
        } else {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [cookbookId, weekStart, accessToken]);

  // Dialog a11y: initial focus, Tab focus-trap, Escape closes, focus returns
  // to whatever triggered the overlay on close/unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
    // Deliberately mount/unmount-only: `onClose` is a fresh closure every
    // render (not memoized by the caller), and re-subscribing on every
    // render would re-run the "grab initial focus" step too — this effect
    // must run exactly once per overlay lifetime, mirroring the fetch
    // effect's own single-purpose shape above.
  }, []);

  function stop(e: React.MouseEvent): void {
    e.stopPropagation();
  }

  function patchLine(ingredientKey: string, patch: Partial<ShoppingListLine>): void {
    setState((prev) =>
      prev.status === "ok"
        ? {
            ...prev,
            fromRecipes: prev.fromRecipes.map((l) =>
              l.ingredientKey === ingredientKey ? { ...l, ...patch } : l,
            ),
          }
        : prev,
    );
  }

  async function toggleLine(line: ShoppingListLine): Promise<void> {
    if (!canEdit) return;
    const next = !line.checked;
    patchLine(line.ingredientKey, { checked: next }); // optimistic
    setMutationError(null);
    try {
      await setShoppingItemState(
        cookbookId,
        { weekStart, ingredientKey: line.ingredientKey, checked: next },
        accessToken,
      );
    } catch (err) {
      patchLine(line.ingredientKey, { checked: line.checked }); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke oppdatere handlelisten. Prøv igjen.");
    }
  }

  async function removeLine(line: ShoppingListLine): Promise<void> {
    if (!canEdit) return;
    patchLine(line.ingredientKey, { removed: true }); // optimistic
    setMutationError(null);
    try {
      await setShoppingItemState(
        cookbookId,
        { weekStart, ingredientKey: line.ingredientKey, removed: true },
        accessToken,
      );
    } catch (err) {
      patchLine(line.ingredientKey, { removed: false }); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke fjerne varen. Prøv igjen.");
    }
  }

  function patchManual(itemId: string, patch: Partial<ManualShoppingItem>): void {
    setState((prev) =>
      prev.status === "ok"
        ? { ...prev, manual: prev.manual.map((m) => (m.id === itemId ? { ...m, ...patch } : m)) }
        : prev,
    );
  }

  async function toggleManualItem(item: ManualShoppingItem): Promise<void> {
    if (!canEdit) return;
    const next = !item.checked;
    patchManual(item.id, { checked: next }); // optimistic
    setMutationError(null);
    try {
      await updateManualShoppingItem(item.id, { checked: next }, accessToken);
    } catch (err) {
      patchManual(item.id, { checked: item.checked }); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke oppdatere varen. Prøv igjen.");
    }
  }

  async function removeManualItem(item: ManualShoppingItem): Promise<void> {
    if (!canEdit) return;
    setState((prev) =>
      prev.status === "ok"
        ? { ...prev, manual: prev.manual.filter((m) => m.id !== item.id) }
        : prev,
    ); // optimistic
    setMutationError(null);
    try {
      await deleteManualShoppingItem(item.id, accessToken);
    } catch (err) {
      setState((prev) =>
        prev.status === "ok" && !prev.manual.some((m) => m.id === item.id)
          ? { ...prev, manual: [...prev.manual, item].sort((a, b) => a.position - b.position) }
          : prev,
      ); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke fjerne varen. Prøv igjen.");
    }
  }

  async function submitManualItem(): Promise<void> {
    if (!canEdit) return;
    const text = manualInput.trim();
    if (!text) return;
    setManualInput("");
    setMutationError(null);

    const tempId = `temp-${crypto.randomUUID()}`;
    const optimistic: ManualShoppingItem = {
      id: tempId,
      text,
      checked: false,
      position: state.status === "ok" ? state.manual.length : 0,
    };
    setState((prev) =>
      prev.status === "ok" ? { ...prev, manual: [...prev.manual, optimistic] } : prev,
    );

    try {
      const created = await addManualShoppingItem(cookbookId, { weekStart, text }, accessToken);
      setState((prev) =>
        prev.status === "ok"
          ? { ...prev, manual: prev.manual.map((m) => (m.id === tempId ? created : m)) }
          : prev,
      );
    } catch (err) {
      setState((prev) =>
        prev.status === "ok"
          ? { ...prev, manual: prev.manual.filter((m) => m.id !== tempId) }
          : prev,
      );
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke legge til varen. Prøv igjen.");
    }
  }

  /** Builds the flat `Handleliste <week> \n\n - line ...` text that copy/share
   * send out — same on-screen order and amount formatting, section headings
   * dropped, `checked`/`removed` lines excluded from both buckets. Returns
   * `null` when nothing is left to send (nothing to copy/share). */
  function buildShoppingListText(): string | null {
    if (state.status !== "ok") return null;
    const lines: string[] = [];
    for (const line of state.fromRecipes) {
      if (line.removed || line.checked) continue;
      const amount = lineAmountLabel(line);
      lines.push(amount ? `- ${amount} ${line.name}` : `- ${line.name}`);
    }
    for (const item of state.manual) {
      if (item.checked) continue;
      lines.push(`- ${item.text}`);
    }
    if (lines.length === 0) return null;
    return `Handleliste ${weekLabel}\n\n${lines.join("\n")}`;
  }

  function showCopyFeedback(kind: CopyFeedback["kind"], text: string, autoClearMs?: number): void {
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
    setCopyFeedback({ kind, text });
    if (autoClearMs) {
      copyFeedbackTimeoutRef.current = setTimeout(() => setCopyFeedback(null), autoClearMs);
    }
  }

  // Read-only actions — available to EVERY member (including read/comment),
  // never gated on `canEdit`.
  function handleCopyList(): void {
    const text = buildShoppingListText();
    if (text === null) {
      showCopyFeedback("info", NOTHING_TO_COPY_MESSAGE);
      return;
    }
    // `navigator.clipboard` is absent in an insecure context / older browser.
    // Reading `.writeText` off `undefined` throws SYNCHRONOUSLY, so `.catch`
    // below would never run and the click handler would blow up instead of
    // showing a message.
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      showCopyFeedback("error", COPY_ERROR_MESSAGE);
      return;
    }
    // Must be called synchronously here, before any await — see the module
    // doc comment (iOS Safari drops the user-gesture context otherwise).
    navigator.clipboard
      .writeText(text)
      .then(() => showCopyFeedback("success", COPY_SUCCESS_MESSAGE, COPY_FEEDBACK_CLEAR_MS))
      .catch(() => showCopyFeedback("error", COPY_ERROR_MESSAGE));
  }

  function handleShareList(): void {
    const text = buildShoppingListText();
    if (text === null) {
      showCopyFeedback("info", NOTHING_TO_COPY_MESSAGE);
      return;
    }
    navigator.share({ title: "Handleliste", text }).catch((err: unknown) => {
      // A cancelled share sheet rejects with AbortError — not an error.
      // `DOMException` isn't reliably `instanceof Error` across
      // environments, so check `.name` directly.
      const name = (err as { name?: unknown } | null)?.name;
      if (name === "AbortError") return;
      showCopyFeedback("error", SHARE_ERROR_MESSAGE);
    });
  }

  function renderRecipeSection(): React.JSX.Element {
    if (state.status !== "ok") return <></>;
    const visible = state.fromRecipes.filter((l) => !l.removed);

    return (
      <div className="shop-section" data-testid="shopping-list-from-recipes">
        <div className="shop-section-label">Fra oppskrifter</div>
        {visible.length === 0 ? (
          <p className="shop-empty" data-testid="shopping-list-empty">
            Ingen måltider planlagt denne uken ennå.
          </p>
        ) : (
          <ul className="shop-line-list">
            {visible.map((line) => {
              const amount = lineAmountLabel(line);
              return (
                <li key={line.ingredientKey}>
                  <div className="shop-line">
                    {canEdit ? (
                      <button
                        type="button"
                        className={`shop-check${line.checked ? " checked" : ""}`}
                        aria-pressed={line.checked}
                        aria-label={`${line.name}${line.checked ? " (avhaket)" : ""}`}
                        data-testid={`shop-line-check-${line.ingredientKey}`}
                        onClick={() => void toggleLine(line)}
                      >
                        {line.checked && <span aria-hidden="true">✓</span>}
                      </button>
                    ) : (
                      <span
                        className={`shop-check shop-check-readonly${line.checked ? " checked" : ""}`}
                        aria-hidden="true"
                      >
                        {line.checked && "✓"}
                      </span>
                    )}
                    <span
                      className={`shop-line-label${line.checked ? " checked" : ""}`}
                      data-testid={`shop-line-label-${line.ingredientKey}`}
                    >
                      {amount ? (
                        <>
                          <span className="shop-line-amount">{amount}</span>{" "}
                          <span className="shop-line-name">{line.name}</span>
                        </>
                      ) : (
                        <span className="shop-line-name">{line.name}</span>
                      )}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        className="shop-line-remove"
                        aria-label={`Fjern ${line.name}`}
                        data-testid={`shop-line-remove-${line.ingredientKey}`}
                        onClick={() => void removeLine(line)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  function renderManualSection(): React.JSX.Element {
    if (state.status !== "ok") return <></>;
    return (
      <div className="shop-section" data-testid="shopping-list-manual">
        <div className="shop-section-label">Annet</div>
        {state.manual.length > 0 && (
          <ul className="shop-line-list">
            {state.manual.map((item) => (
              <li key={item.id}>
                <div className="shop-line">
                  {canEdit ? (
                    <button
                      type="button"
                      className={`shop-check${item.checked ? " checked" : ""}`}
                      aria-pressed={item.checked}
                      aria-label={`${item.text}${item.checked ? " (avhaket)" : ""}`}
                      data-testid={`shop-manual-check-${item.id}`}
                      onClick={() => void toggleManualItem(item)}
                    >
                      {item.checked && <span aria-hidden="true">✓</span>}
                    </button>
                  ) : (
                    <span
                      className={`shop-check shop-check-readonly${item.checked ? " checked" : ""}`}
                      aria-hidden="true"
                    >
                      {item.checked && "✓"}
                    </span>
                  )}
                  <span
                    className={`shop-line-label${item.checked ? " checked" : ""}`}
                    data-testid={`shop-manual-label-${item.id}`}
                  >
                    <span className="shop-line-name">{item.text}</span>
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="shop-line-remove"
                      aria-label={`Fjern ${item.text}`}
                      data-testid={`shop-manual-remove-${item.id}`}
                      onClick={() => void removeManualItem(item)}
                    >
                      ×
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <form
            className="shop-manual-add"
            onSubmit={(e) => {
              e.preventDefault();
              void submitManualItem();
            }}
          >
            <input
              type="text"
              className="input shop-manual-input"
              placeholder="Legg til vare …"
              aria-label="Legg til vare"
              data-testid="shop-manual-input"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
            />
            <button
              type="submit"
              className="shop-manual-add-btn"
              aria-label="Legg til vare"
              data-testid="shop-manual-add"
            >
              +
            </button>
          </form>
        )}
      </div>
    );
  }

  function renderBody(): React.JSX.Element {
    if (state.status === "loading") {
      return (
        <p className="shop-state" data-testid="shopping-list-loading">
          Laster handleliste …
        </p>
      );
    }
    if (state.status === "unauthorized") {
      return (
        <div className="shop-state" data-testid="shopping-list-unauthorized">
          <p>Økten er utløpt.</p>
          <button type="button" className="btn btn-primary" onClick={onSessionExpired}>
            Logg inn igjen
          </button>
        </div>
      );
    }
    if (state.status === "error") {
      return (
        <p className="shop-state" data-testid="shopping-list-error" role="alert">
          Kunne ikke laste handlelisten.
        </p>
      );
    }
    return (
      <>
        {mutationError && (
          <p
            className="shop-mutation-error"
            data-testid="shopping-list-mutation-error"
            role="alert"
          >
            {mutationError}
          </p>
        )}
        {renderRecipeSection()}
        {renderManualSection()}
      </>
    );
  }

  const canSend = state.status === "ok";
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div className="dlg-overlay shop-overlay" data-testid="shopping-list-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="dlg-panel shop-list-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shopping-list-title"
        data-testid="shopping-list-dialog"
        onClick={stop}
      >
        <div className="shop-list-head">
          <div>
            <div className="dlg-title" id="shopping-list-title">
              Handleliste
            </div>
            <div className="shop-week-label" data-testid="shopping-list-week">
              {weekLabel}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="shop-list-close"
            aria-label="Lukk handleliste"
            data-testid="shopping-list-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="shop-list-body">{renderBody()}</div>

        {canSend && (
          <div className="shop-list-send-row" data-testid="shopping-list-send-row">
            <div className="shop-list-send-buttons">
              <button
                type="button"
                className="btn btn-ghost shop-list-send-btn"
                data-testid="shopping-list-copy"
                onClick={handleCopyList}
              >
                Kopier
              </button>
              {canShare && (
                <button
                  type="button"
                  className="btn btn-ghost shop-list-send-btn"
                  data-testid="shopping-list-share"
                  onClick={handleShareList}
                >
                  Del
                </button>
              )}
            </div>
            {copyFeedback && (
              <p
                className={`shop-list-send-feedback shop-list-send-feedback-${copyFeedback.kind}`}
                data-testid="shopping-list-copy-feedback"
                role={copyFeedback.kind === "error" ? "alert" : "status"}
              >
                {copyFeedback.text}
              </p>
            )}
          </div>
        )}

        <div className="dlg-actions">
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="shopping-list-done"
            onClick={onClose}
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}
